import React, { useState, useEffect } from 'react';
import { 
  Badge, IconButton, Menu, MenuItem, Typography, 
  Box, Divider, CircularProgress, Button, Tooltip 
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import axios from 'axios';
import { API_BASE_URL } from '../../apiConfig';

const NOTIFICATIONS_API = API_BASE_URL.replace('/espacios', '/notifications');

export default function NotificationsMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const open = Boolean(anchorEl);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(NOTIFICATIONS_API);
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    } catch (e) {
      console.error("Error fetching notifications", e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (!open && notifications.length === 0) {
      fetchNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axios.patch(`${NOTIFICATIONS_API}/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error("Error marking notification as read", e);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.patch(`${NOTIFICATIONS_API}/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error("Error marking all notifications as read", e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Ensure UTC
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('es-DO', { 
        day: 'numeric', month: 'short', 
        hour: 'numeric', minute: '2-digit', hour12: true 
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton color="inherit" onClick={handleClick}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 350, maxHeight: 500 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Notificaciones</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllAsRead}>
              Marcar todo como leído
            </Button>
          )}
        </Box>
        <Divider />
        {loading && notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No tienes notificaciones</Typography>
          </Box>
        ) : (
          notifications.map(notif => (
            <MenuItem 
              key={notif.id} 
              onClick={() => { if (!notif.is_read) handleMarkAsRead(notif.id); }}
              sx={{ 
                flexDirection: 'column', 
                alignItems: 'flex-start',
                bgcolor: notif.is_read ? 'transparent' : 'action.selected',
                whiteSpace: 'normal',
                p: 2,
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              <Typography variant="subtitle2" fontWeight={notif.is_read ? 'normal' : 'bold'}>
                {notif.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {notif.message}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                {formatDate(notif.created_at)}
              </Typography>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
